/// <reference types="@angular/localize" />

import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, Injector } from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { MatPasswordStrengthModule } from '@angular-material-extensions/password-strength';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AngularMaterialModule } from './app/shared/angular-material.module';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OAuthModule } from 'angular-oauth2-oidc';
import { CoreModule } from './app/core/core.module';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Router, withDebugTracing } from '@angular/router';
import { CustomErrorHandler } from './app/core/services/CustomErrorHandler';
import { AuthInterceptor } from './app/core/auth.interceptor';
import {
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
  provideHttpClient,
  HttpClient,
  withFetch,
} from '@angular/common/http';
import { HTTPStatus, HTTPListener } from './app/core/progress.inteceptor';
import { CookieService } from 'ngx-cookie-service';
import { OnboardedGuardService } from './app/core/onboarded-guard.service';
import { AuthGuardService } from './app/core/auth-guard.service';
import { PropertiesGuardService } from './app/core/properties-guard.service';
import { PropertiesOrOnboardedGuardService } from './app/core/properties-or-onboarded-guard.service';
import { WindowRef } from './app/user/components/registration/WindowRef';
import * as Sentry from '@sentry/angular-ivy';
import { routes } from './app/app.routes';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { IconsFactory } from '@shared/services/icon/icon.service';
import { VersionCheckService } from '@core/services/version-check.service';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n_translations/',
        suffix: '.json'
      })
    }),
    importProvidersFrom(
      BrowserModule,
      CoreModule.forRoot(),
      OAuthModule.forRoot({
        resourceServer: {
          allowedUrls: [environment.apiBaseUrl],
          sendAccessToken: true,
        },
      }),
      FontAwesomeModule,
      AngularMaterialModule,
      FormsModule,
      ReactiveFormsModule,
      MatPasswordStrengthModule.forRoot()
    ),
    WindowRef,
    AuthGuardService,
    OnboardedGuardService,
    PropertiesGuardService,
    PropertiesOrOnboardedGuardService,
    CookieService,
    HTTPStatus,
    { provide: HTTP_INTERCEPTORS, useClass: HTTPListener, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: 'BASE_API_URL', useValue: environment.apiBaseUrl },
    { provide: Window, useValue: window },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.siteKey },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideAnimations(),
    provideLottieOptions({ player: () => player }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: APP_INITIALIZER,
      useFactory: (versionCheckService: VersionCheckService) => () => versionCheckService.initialize(),
      deps: [VersionCheckService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => {
        return () => injector.get(IconsFactory).init();
      },
      multi: true,
      deps: [Injector],
    },
  ],
}).catch(err => {
  let error = new Error('Error bootstrapping the application');
  console.error(error);
  console.error(err);
  throw error;
});
